import { apiCall, isBackendConfigured } from './api-call'
import {
  CreateQuickPassSavedSearchInput,
  QuickPassSavedSearch,
  RunQuickPassSavedSearchResult,
  UpdateQuickPassSavedSearchInput,
} from '../_types/quickpass-saved-search'

const warnFallback = (operation: string, error: unknown): void => {
  console.warn(
    `[quickpass-saved-searches] backend ${operation} failed —`,
    error,
  )
}

/**
 * Lists every saved search for the renter, newest first. Returns an empty
 * array when the backend is unreachable — saved searches have no
 * localStorage seed, the renter just sees an empty list in dev-bypass
 * mode until they create one (and even that would no-op locally).
 *
 * @param {string} quickPassUserId
 * @returns {Promise<QuickPassSavedSearch[]>}
 */
const getQuickPassSavedSearchesForUser = async (
  quickPassUserId: string,
): Promise<QuickPassSavedSearch[]> => {
  if (isBackendConfigured() === false) {
    return []
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getsavedsearches',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassSavedSearch[]
  } catch (error) {
    warnFallback('getSavedSearches', error)
    return []
  }
}

/**
 * Creates a new saved search. Throws on backend failure so callers can
 * surface a real error in the UI rather than silently swallowing.
 *
 * @param {CreateQuickPassSavedSearchInput} input
 * @returns {Promise<QuickPassSavedSearch>}
 */
const createQuickPassSavedSearch = async (
  input: CreateQuickPassSavedSearchInput,
): Promise<QuickPassSavedSearch> => {
  const response = await apiCall({
    group: 'quickpass',
    command: 'createsavedsearch',
    data: {
      quickPassUserId: input.quickPassUserId,
      name: input.name,
      criteria: input.criteria,
      frequency: input.frequency,
      notifyOnMatch: input.notifyOnMatch,
    },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return JSON.parse(response.data) as QuickPassSavedSearch
}

/**
 * Patches an existing saved search.
 *
 * @param {UpdateQuickPassSavedSearchInput} input
 * @returns {Promise<QuickPassSavedSearch>}
 */
const updateQuickPassSavedSearch = async (
  input: UpdateQuickPassSavedSearchInput,
): Promise<QuickPassSavedSearch> => {
  const response = await apiCall({
    group: 'quickpass',
    command: 'updatesavedsearch',
    data: {
      savedSearchId: input.savedSearchId,
      quickPassUserId: input.quickPassUserId,
      updates: input.updates,
    },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return JSON.parse(response.data) as QuickPassSavedSearch
}

/**
 * Deletes a saved search.
 *
 * @param {string} savedSearchId
 * @param {string} quickPassUserId
 * @returns {Promise<void>}
 */
const deleteQuickPassSavedSearch = async (
  savedSearchId: string,
  quickPassUserId: string,
): Promise<void> => {
  const response = await apiCall({
    group: 'quickpass',
    command: 'deletesavedsearch',
    data: { savedSearchId, quickPassUserId },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }
}

/**
 * Runs a saved search and writes preference-match notifications for any
 * new matches found.
 *
 * @param {string} savedSearchId
 * @param {string} quickPassUserId
 * @returns {Promise<RunQuickPassSavedSearchResult>}
 */
const runQuickPassSavedSearch = async (
  savedSearchId: string,
  quickPassUserId: string,
): Promise<RunQuickPassSavedSearchResult> => {
  const response = await apiCall({
    group: 'quickpass',
    command: 'runsavedsearch',
    data: { savedSearchId, quickPassUserId },
  })

  if (response.code !== 200) {
    throw new Error(response.message)
  }

  return JSON.parse(response.data) as RunQuickPassSavedSearchResult
}

export {
  getQuickPassSavedSearchesForUser,
  createQuickPassSavedSearch,
  updateQuickPassSavedSearch,
  deleteQuickPassSavedSearch,
  runQuickPassSavedSearch,
}
